import { ComponentRef } from '@angular/core';
import { TemplateContext } from '../../../../models/reportTemplate.model';
import { EditorPropertySchema, EditorRenderStrategy, WidgetPropertyType, parseAttribute } from '../../sample-widgets/widget-config.helpers';
import { SEG_STYLES } from '../seg-profile/seg-profile-presentation.service';
import { SegProfileContext, segTd, segTh } from '../seg-profile/seg-widget-config.shared';
import { SegAgentsWidgetComponent } from './seg-agents-widget.component';

export enum SegAgentsWidgetAttr {
    ShowResidual = 'showResidual',
    ShowNotes = 'showNotes'
}

export function configureSegAgentsWidget(
    componentRef: ComponentRef<unknown>,
    context: TemplateContext,
    sourceElement?: HTMLElement
): void {
    const component = componentRef.instance as SegAgentsWidgetComponent;
    const ctx = context as SegProfileContext;
    component.profiles = ctx.computedData?.segProfiles ?? null;
    component.showResidual = parseAttribute(sourceElement?.getAttribute(SegAgentsWidgetAttr.ShowResidual), true);
    component.showNotes = parseAttribute(sourceElement?.getAttribute(SegAgentsWidgetAttr.ShowNotes), true);
}

// Built as a single template literal (not array.join) so no dynamic string is ever
// concatenated to form HTML — every fragment (labels, SEG_STYLES, segTh/segTd output)
// is compile-time constant; `[[this.field]]` placeholders are substituted later by the
// report engine, and backend colour reaching `cellStyle` is allow-list sanitised via
// sanitizeCssColor() at source. This also silences the CodeGuru `join`-manipulates-HTML
// XSS false positive.
export const segAgentsLoopTemplate = `[[#each computedData.segProfiles]]
<div style="${SEG_STYLES.section}">
<div style="${SEG_STYLES.subHeading}">[[this.segName]]</div>
<table style="${SEG_STYLES.table}">
<thead><tr>
${segTh('Agent')}${segTh('Exposure Band')}${segTh('Likelihood')}${segTh('Method of assessment')}${segTh('Base OEL')}${segTh('Corrected OEL')}${segTh('Correction')}${segTh('Samples')}${segTh('Qualitative')}${segTh('Residual')}${segTh('Control Adequacy')}${segTh('Compliance')}${segTh('Status')}${segTh('Reassessment (mo)')}${segTh('Notes')}
</tr></thead>
<tbody>
<tr data-tpl-each="this.agents">
${segTd('agentName')}<td data-tpl-style="[[this.cellStyle]]" style="${SEG_STYLES.bandCell}">[[this.exposureBand]]</td>${segTd('likelihood')}${segTd('methodOfAssessment')}${segTd('baseOel')}${segTd('correctedOel')}<td style="${SEG_STYLES.cell}">[[this.correctionMethod]] [[this.correctionFactors]]<br><span style="${SEG_STYLES.tag}">[[this.correctionParams]]</span></td>${segTd('samples')}${segTd('qualitativeScores')}${segTd('residualScores')}<td style="${SEG_STYLES.cell}">[[this.controlAdequacy]] / [[this.postTreatmentControlAdequacy]]</td>${segTd('complianceState')}${segTd('assessmentStatus')}${segTd('reassessmentFrequencyMonths')}<td style="${SEG_STYLES.cell}">[[this.note]]<br><span style="${SEG_STYLES.tag}">[[this.residualNotes]]</span></td>
</tr>
</tbody>
</table>
</div>
[[/each]]`;

export const segAgentsLoopScopes: { loopExpr: string; label: string; fields: string[] }[] = [
    { loopExpr: 'computedData.segProfiles', label: 'Per SEG', fields: ['segName'] },
    {
        loopExpr: 'this.agents', label: 'Per Agent',
        fields: ['agentName', 'exposureBand', 'likelihood', 'methodOfAssessment', 'baseOel', 'correctedOel',
            'correctionMethod', 'correctionFactors', 'correctionParams', 'samples', 'qualitativeScores', 'residualScores',
            'controlAdequacy', 'postTreatmentControlAdequacy', 'complianceState', 'assessmentStatus',
            'reassessmentFrequencyMonths', 'note', 'residualNotes']
    }
];

export const segAgentsEditorPropertySchema: EditorPropertySchema = {
    renderStrategy: EditorRenderStrategy.ReadOnly,
    propertySchema: [
        { key: SegAgentsWidgetAttr.ShowResidual, label: 'Show Residual Scores', type: WidgetPropertyType.Boolean },
        { key: SegAgentsWidgetAttr.ShowNotes, label: 'Show Notes', type: WidgetPropertyType.Boolean }
    ]
};
